import React, { ReactElement, ComponentType, Component } from 'react';

/**
 * Extracts types of component, useful if you want spread props to
 * another component, no need to export its props interface, for example:
 *
 * ```typescript
 * const C1 = ({ a, b }: { a: string }) => ...
 *
 * // another file:
 * interface IProps extends ExtractProps<typeof C1> {
 *   c: string;
 * }
 * const C2 = ({ c, ...props }: IProps) => <div smth={c}><C1 {...props} /></div>;
 * ```
 *
 * @deprecated Unless you need to support generic components - use {@link React.ComponentProps}
 */
export type ExtractProps<T> =
  T extends ComponentType<infer P> ? P :
  T extends Component<infer P> ? P : never;

/**
 * Convenience type for picking props from another component
 */
export type PickProps<T extends React.JSXElementConstructor<any>, P extends keyof React.ComponentProps<T>> = Pick<
  React.ComponentProps<T>,
  P
>;

export type Subtract<P extends O, O> = Omit<P, keyof O>;

/**
 * Make all properties in T writable
 */
export type Writable<T> = { -readonly [K in keyof T]: T[K] };

/**
 * Returns the argument type of a unary function, or null if the
 * function takes no arguments.
 *
 * The return type of the unary function is required to be void by
 * default, but can be overridden.
 *
 * ```typescript
 * OptionalUnaryArgType<() => void>                     // null
 * OptionalUnaryArgType<(x: number) => void>            // number
 *
 * // Functions must by nullary or unary
 * OptionalUnaryArgType<(x: number, y: number) => void> // never
 *
 * // Return type can be checked
 * OptionalUnaryArgType<(x: number) => void, number>    // never
 * ```
 */
export type OptionalUnaryArgType<T, R = void> =
  T extends () => R ? null :
  T extends (x: infer A) => R ? A :
  never;

/**
 * Returns the set of keys in an interface which conform to a certain
 * type.
 *
 * ```typescript
 * interface IFace {
 *   name: string;
 *   age: number;
 *   height: number;
 * }
 *
 * KeysWithType<IFace, string> // 'name'
 * KeysWithType<IFace, number> // 'age' | 'height'
 * ```
 */
export type KeysWithType<Interface, Type> = {
  [Key in keyof Interface]: Interface[Key] extends Type ? Key : never;
}[keyof Interface];

/**
 * Like Pick but it will only leave fields that conform to a certain
 * type.
 *
 * ```typescript
 * interface IFace {
 *   name: string;
 *   age: number;
 *   height: number;
 * }
 *
 * PickByType<IFace, string> // { name: string }
 * PickByType<IFace, number> // { age: number, height: number }
 * ```
 */
export type PickByType<Interface, Type> = Pick<Interface, KeysWithType<Interface, Type>>;

/*
 * Return a type for something that consumes a providers value
 */
export type ConsumerValue<T extends React.ComponentType<React.ConsumerProps<any>>> =
  T extends React.ComponentType<React.ConsumerProps<infer P>> ? P : never;

/**
 * Create a type without any methods
 */
export type WithoutMethods<Type> = Omit<Type, KeysWithType<Type, (args: any) => any>>;

/**
 * Keep all non-callable fields intact but replace all callable
 * fields with `Stub`
 */
export type StubMethods<Type, Stub> = {
  // eslint-disable-next-line @typescript-eslint/ban-types
  [K in keyof Type]: Type[K] extends Function ? Stub: Type[K];
};

/**
 * Makes all methods from an interface async, ignores any non-methods
 */
export type PromisifyInterface<T> = {
  // eslint-disable-next-line @typescript-eslint/ban-types
  [K in keyof Pick<T, KeysWithType<T, Function>>]:
    T[K] extends (...a: infer A) => infer B
    ? (...a: A) => (B extends Promise<unknown> ? B : Promise<B>)
    : never;
};

export type ValuesOf<T> = T[keyof T];

/**
 * A type similar to FunctionComponent that is used to signify that something is a render function
 * rather than a component. Render function is called directly (onRenderSomething()) rather than
 * being rendered with JSX: <OnRenderSomething />.
 */
export interface IRenderFunction<P> {
  (props: P): ReactElement | null;
}

/**
 * From https://stackoverflow.com/questions/39419170/how-do-i-check-that-a-switch-block-is-exhaustive-in-typescript
 *
 * Can be used with a switch/case block to make sure that all cases of the given
 * value are handled.
 */
export function assertUnreachable(value: never): never {
  throw new Error('Unreachable code reached');
}

/**
 * Create a type guard that will throw if the given guard returns false.
 * This is meant to be used in situations where the type is always expected to be correct.
 *
 * @param guard the original typeguard.
 * @returns modified typeguard which throws if the type check fails.
 */
export function makeThrowingTypeGuard<T>(guard: (x: unknown) => x is T) {
  return (x: unknown): x is T => {
    if (guard(x)) {
      return true;
    } else {
      throw new Error(`Expected ${x} to pass ${guard.name} typeguard`);
    }
  };
}

/**
 * A utility type to make some properties optional
 */
export type Optional<T, K extends keyof T> = Omit<T, K> & Partial<T>;

/**
 * Like Omit but distributed over union types
 *
 * Avoids awkward truncating the union type to its common elements
 *
 * ```
 * type A = { a: number; b: number; c: number; };
 * type B = { a: number; b: number; d: number; };
 *
 * type ABO = Omit<A | B, 'a'>; // { b: number }
 * type DABO = DistributiveOmit<A | B, 'a'>; // Omit<A, 'a'> | Omit<B, 'a'>
 * ```
 *
 * Borrowed from:
 * https://davidgomes.com/pick-omit-over-union-types-in-typescript/
 */
export type DistributiveOmit<T, K extends keyof T> = T extends unknown
  ? Omit<T, K>
  : never;
